import axios, { AxiosError, AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import i18next from 'i18next';
import Cookie from 'js-cookie';
import { ApiError, ApiResponse } from '@/types/api';
import { APIENDPOINT_AUTH, APIENDPOINT_LOGOUT } from '@/constants/api_endpoints';
import { AR_LOCK_EVENT } from '@/components/AnnualReportLockModal/AnnualReportLockModal';

export enum Methods {
  POST = 'POST',
  GET = 'GET',
  PUT = 'PUT',
  DELETE = 'DELETE'
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
});

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // is this an unauthorized? Session has probably expired.
    if (error.response.status === 401) {
      delete api.defaults.headers.authorization;
      Cookie.remove('tuempresaenestonia_auth');
      window.location.href = `${import.meta.env.VITE_API_URL}${APIENDPOINT_AUTH}${APIENDPOINT_LOGOUT}`;
    }
    if (
      error.response &&
      error.response.status >= 500 &&
      error.response.status < 600 &&
      error.config?.responseType !== 'blob'
    ) {
      toast.error(error.response);
      console.error('Server Error:', error.response);
    }
    return Promise.reject(error);
  }
);

const fetchData = async (
  method: Methods,
  url: string,
  body?: any,
  errorMessage?: string,
  showErrorToast: boolean = true
) => {
  try {
    let requestUrl = url;

    if (method === 'GET' && body) {
      const queryParams = new URLSearchParams(body).toString();
      requestUrl = `${url}?${queryParams}`;
    }

    const response: AxiosResponse<ApiResponse<any>> = await api(
      import.meta.env.VITE_API_URL + requestUrl,
      {
        data: body,
        method
      }
    );
    if (!response.status || response.data?.error) {
      throw new Error('Request failed');
    }
    return await response.data;
  } catch (err) {
    const error = err as AxiosError<{ error?: string; error_code?: string; message?: string }>;
    const errorCode = error?.response?.data?.error_code;
    const responseData = error?.response?.data;
    const { response: { data: { message: errorMessageApi = '' } = {} } = {} } = error as any;

    if (
      error?.response?.status === 409 &&
      (responseData as any)?.error === 'TRANSACTION_LOCKED_BY_ANNUAL_REPORT'
    ) {
      window.dispatchEvent(new CustomEvent(AR_LOCK_EVENT, { detail: responseData }));
      throw new ApiError(
        'Transaction locked by Annual Report',
        responseData,
        'TRANSACTION_LOCKED_BY_ANNUAL_REPORT'
      );
    }

    if (showErrorToast || !responseData) {
      toast.error(errorMessage || errorMessageApi || i18next.t('default_api_error_message'), {
        toastId: errorMessage || errorMessageApi
      });
    }

    const enrichedError = new ApiError(
      'The request could not be completed.',
      responseData,
      errorCode
    );
    throw enrichedError;
  }
};

export const fetchDataRaw = async (
  method: Methods,
  url: string,
  body?: any
): Promise<ApiResponse<any>> => {
  let requestUrl = url;

  if (method === 'GET' && body) {
    const queryParams = new URLSearchParams(body).toString();
    requestUrl = `${url}?${queryParams}`;
  }

  const config: any = {
    method,
    data: body
  };

  if (body instanceof FormData) {
    config.headers = { 'Content-Type': undefined };
  }

  const response: AxiosResponse<ApiResponse<any>> = await api(
    import.meta.env.VITE_API_URL + requestUrl,
    config
  );

  return response.data;
};

export const fetchDataBlob = async (
  url: string,
  body: any,
  filename: string
): Promise<{ queued: boolean } | void> => {
  try {
    const response = await api(import.meta.env.VITE_API_URL + url, {
      method: Methods.POST,
      data: body,
      responseType: 'blob'
    });

    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await (response.data as Blob).text();
      try {
        const parsed = JSON.parse(text);
        if (parsed.queued === true) {
          return { queued: true };
        }
      } catch {
        // non-JSON body, fall through to download
      }
    }

    const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  } catch (err: any) {
    if (err?.response?.data instanceof Blob) {
      const text = await err.response.data.text();
      let apiMessage: string | null = null;
      try {
        const parsed = JSON.parse(text);
        apiMessage = parsed?.message ?? null;
      } catch {
        // not JSON — ignore, fall through to generic error
      }
      if (apiMessage) throw new Error(apiMessage);
    }
    throw err;
  }
};

export default fetchData;
